import { ConfirmationRequestType } from '../../features/auth/thunks/auth.thunk'
import { publicApiRequest } from '../../lib/hooks/axios-instance'
import {
  LoginDataType,
  OtpResetVerificationType,
  RegistrationDataType,
  ResetPasswordDataType,
  CreateAccountDraftRequest,
  SetPasswordRequest,
  VerifyAndCompleteRequest,
} from '../../types/auth.types'
import { UserEntityType } from '../../types/models/user-model.types'

/**
 * Authentication service
 */

export type OtpVerificationType = Pick<RegistrationDataType, 'otpCode' | 'email'>
export type refreshTokenType = { refreshToken: string }

export class AuthenticationService {
  /**
   * Get confirmation code
   * @returns Promise<any>
   */
  public async getConfirmationCode(data: ConfirmationRequestType) {
    return await (
      await publicApiRequest()
    ).post<{
      description: string
      success: boolean
    }>('/auth/request-email-verification', {
      email: data.email,
      name: data.name,
    })
  }

  /**
   * Create a User Account
   * @returns Promise<any>
   */
  public async createUserAccount(data: Omit<RegistrationDataType, 'confirmPassword'>) {
    console.log('🔵 [AUTH SERVICE] Creating user account with data:', data)
    console.log('🔵 [AUTH SERVICE] Backend URL will be retrieved from token service')

    const axiosInstance = await publicApiRequest()
    console.log('🔵 [AUTH SERVICE] Axios instance created, base URL:', axiosInstance.defaults.baseURL)
    console.log('🔵 [AUTH SERVICE] Making POST request to:', '/auth/create-account')
    console.log('🔵 [AUTH SERVICE] Request payload:', JSON.stringify(data, null, 2))

    return await axiosInstance.post<{
      message?: string
      description?: string
      success: boolean
    }>('/auth/create-account', data)
  }

  /**
   * Verify otp after register
   * @returns Promise<any>
   */
  public async emailVerificationCode(data: OtpVerificationType) {
    return await (
      await publicApiRequest()
    ).post<{
      description: string
      success: boolean
    }>('/auth/verify-email-otp', data)
  }

  /**
   * Verify email user created by admin
   * @returns Promise<any>
   */
  public async emailVerificationUserCode(data: OtpVerificationType) {
    return await (
      await publicApiRequest()
    ).put<{
      description: string
      success: boolean
    }>('/auth/verify-email', data)
  }

  /**
   * Get email confirmation mainly for register users
   * @returns Promise<any>
   */
  public async resendEmailConfirmationCode(data: ConfirmationRequestType) {
    return await (
      await publicApiRequest()
    ).post<{
      description: string
      success: boolean
    }>('/auth/resend-email-verification', {
      email: data.email,
    })
  }

  /**
   * Login a user
   * @returns Promise<any>
   */
  public async loginuser(data: LoginDataType) {
    return await (
      await publicApiRequest()
    ).post<{
      description: string
      success: string
    }>('/auth/login', data)
  }

  /**
   * Verify otp after login
   * @returns Promise<any>
   */
  public async verifyOTPAfterLogin(data: OtpVerificationType & {
    device?: string
    browser?: string
    os?: string
    platform?: string
    ip?: string
    location?: string
  }) {
    return await (
      await publicApiRequest()
    ).put<{
      accessToken: string
      refreshToken: string
      userData: UserEntityType
    }>('/auth/otp-login', data)
  }

  /**
   * get otp code to verify email and reset password
   * @returns Promise<any>
   */
  public async forgotPassword(data: ResetPasswordDataType) {
    return await (
      await publicApiRequest()
    ).post<{
      description: string
      success: boolean
    }>('/auth/forgot-password', {
      email: data.email,
    })
  }

  /**
   * Verify otp after login
   * @returns Promise<any>
   */
  public async resetPassword(data: ResetPasswordDataType) {
    return await (
      await publicApiRequest()
    ).put<{
      description: string
      success: string
    }>('/auth/reset-password', data)
  }

  /**
   * Verify reset otp code
   * @returns Promise<any>
   */
  public async verifyResetOtpCode(data: OtpResetVerificationType) {
    return await (
      await publicApiRequest()
    ).post<{
      description: string
      success: boolean
    }>('/auth/verify-email-reset-otp', data)
  }

  /**
   * refresh user token
   * @returns Promise<any>
   */
  public async refreshToken(data: refreshTokenType) {
    return await (
      await publicApiRequest()
    ).put<{
      accessToken: string
      refreshToken: string
      userData: UserEntityType
    }>('/auth/refresh-token', data)
  }

  /**
   * Check if phone number is available for registration
   * @param countryCode - The country calling code (e.g., +237)
   * @param phoneNumber - The phone number without country code
   * @returns Promise<{ available: boolean, message: string }>
   */
  public async checkPhoneAvailability(countryCode: string, phoneNumber: string) {
    console.log('🟡 [STEP 9 - AUTH SERVICE] checkPhoneAvailability called')
    console.log('🟡 [STEP 9] countryCode parameter:', countryCode)
    console.log('🟡 [STEP 9] phoneNumber parameter:', phoneNumber)

    const axiosInstance = await publicApiRequest()
    const params = { countryCode, phoneNumber }

    console.log('🟡 [STEP 10] Axios base URL:', axiosInstance.defaults.baseURL)
    console.log('🟡 [STEP 10] Request params object:', params)
    console.log('🟡 [STEP 10] Making GET request to: /auth/check-phone-availability')

    return await axiosInstance.get<{
      available: boolean
      message: string
    }>('/auth/check-phone-availability', {
      params: params,
    })
  }

  /**
   * Check if email is available for registration
   * @param email - The email address to check
   * @returns Promise<{ available: boolean, message: string }>
   */
  public async checkEmailAvailability(email: string) {
    console.log('📧 [EMAIL CHECK] checkEmailAvailability called')
    console.log('📧 [EMAIL CHECK] email parameter:', email)

    const axiosInstance = await publicApiRequest()
    const params = { email }

    console.log('📧 [EMAIL CHECK] Making GET request to: /auth/check-email-availability')

    return await axiosInstance.get<{
      available: boolean
      message: string
    }>('/auth/check-email-availability', {
      params: params,
    })
  }

  // ========== TWO-PHASE REGISTRATION METHODS ==========

  /**
   * Create draft account (Phase 1 of registration)
   * @returns Promise with userId and otpSent status
   */
  public async createDraftAccount(data: CreateAccountDraftRequest) {
    console.log('🔵 [AUTH SERVICE] Creating draft account:', data)
    const axiosInstance = await publicApiRequest()

    return await axiosInstance.post<import('../../types/auth.types').CreateAccountDraftResponse>(
      '/auth/create-account-draft',
      data
    )
  }

  /**
   * Set password on draft account (Phase 2 of registration)
   * @returns Promise with success status
   */
  public async setPassword(data: SetPasswordRequest) {
    console.log('🔵 [AUTH SERVICE] Setting password for:', data.email)
    const axiosInstance = await publicApiRequest()

    return await axiosInstance.put<import('../../types/auth.types').SetPasswordResponse>(
      '/auth/set-password',
      data
    )
  }

  /**
   * Verify OTP and complete registration (Phase 3 of registration)
   * Also used for login account recovery
   * @returns Promise with tokens (if fromLogin=true) and user data
   */
  public async verifyAndComplete(data: VerifyAndCompleteRequest) {
    console.log('🔵 [AUTH SERVICE] Verifying and completing account:', data.email, 'fromLogin:', data.fromLogin)
    const axiosInstance = await publicApiRequest()

    return await axiosInstance.post<import('../../types/auth.types').VerifyAndCompleteResponse>(
      '/auth/verify-and-complete',
      data
    )
  }
}
